import React, { useEffect, useState } from "react";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";

const listeners = new Set();
let pendingCount = 0;
let installed = false;

const emit = () => {
  listeners.forEach((listener) => listener(pendingCount));
};

const increment = () => {
  pendingCount += 1;
  emit();
};

const decrement = () => {
  pendingCount = Math.max(0, pendingCount - 1);
  emit();
};

export const installAxiosNetworkLoader = (instance = axios) => {
  if (installed) return;
  installed = true;

  instance.interceptors.request.use(
    (config) => {
      increment();
      return config;
    },
    (error) => {
      decrement();
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      decrement();
      return response;
    },
    (error) => {
      decrement();
      return Promise.reject(error);
    }
  );

  if (typeof window !== "undefined" && typeof window.fetch === "function") {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (...args) => {
      increment();
      return originalFetch(...args)
        .then((response) => {
          decrement();
          return response;
        })
        .catch((error) => {
          decrement();
          throw error;
        });
    };
  }
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "0",
  padding: "24px",
  background:
    "radial-gradient(circle at center, rgba(255,255,255,0.72) 0%, rgba(244,248,247,0.84) 45%, rgba(230,237,236,0.94) 100%)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
};

const cardStyle = {
  width: "min(380px, calc(100vw - 40px))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "12px",
  padding: "26px 28px 24px",
  borderRadius: "22px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(249,252,252,0.985) 100%)",
  boxShadow:
    "0 22px 60px rgba(14, 72, 69, 0.10), 0 3px 10px rgba(15, 126, 119, 0.05)",
  border: "1px solid rgba(15, 126, 119, 0.09)",
  position: "relative",
  overflow: "hidden",
};

const accentStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "3px",
  background: "linear-gradient(90deg, #0F7E77 0%, #7BC0BA 50%, #0F7E77 100%)",
};

const spinnerWrapStyle = {
  width: 68,
  height: 68,
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(15,126,119,0.14) 0%, rgba(15,126,119,0.04) 58%, rgba(255,255,255,0) 100%)",
  boxShadow: "inset 0 0 0 1px rgba(15, 126, 119, 0.06)",
};

const titleStyle = {
  margin: 0,
  fontSize: "15px",
  fontWeight: 800,
  letterSpacing: "0.01em",
  color: "#0F7E77",
};

const subtitleStyle = {
  margin: 0,
  fontSize: "12px",
  lineHeight: 1.45,
  color: "#6a7675",
  textAlign: "center",
  maxWidth: "31ch",
};

const metaRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  marginTop: "2px",
};

const metaDotStyle = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#0F7E77",
  opacity: 0.8,
  animation: "nlBeat 1.4s infinite ease-in-out",
};

const metaTextStyle = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "#6f7f7e",
};

export function NetworkLoaderOverlay({ message = "Loading data...", visible }) {
  const [count, setCount] = useState(pendingCount);

  useEffect(() => {
    const listener = (nextCount) => setCount(nextCount);
    listeners.add(listener);
    setCount(pendingCount);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const shouldShow = typeof visible === "boolean" ? visible : count > 0;
  if (!shouldShow) return null;

  return (
    <div style={overlayStyle} aria-live="polite" aria-busy="true">
      <div style={cardStyle}>
        <div style={accentStyle} />
        <div style={spinnerWrapStyle}>
          <CircularProgress size={38} thickness={4.5} style={{ color: "#0F7E77" }} />
        </div>
        <p style={titleStyle}>{message}</p>
        <p style={subtitleStyle}>Fetching the latest data and preparing the results.</p>
        <div style={metaRowStyle}>
          <span style={{ ...metaDotStyle, animationDelay: "0s" }} />
          <span style={{ ...metaDotStyle, animationDelay: "0.18s" }} />
          <span style={{ ...metaDotStyle, animationDelay: "0.36s" }} />
          <span style={metaTextStyle}>Syncing</span>
        </div>
      </div>
      <style>{`
        @keyframes nlPulse {
          0% { transform: scale(0.92); opacity: 0.58; box-shadow: 0 0 0 0 rgba(15, 126, 119, 0.18); }
          70% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 10px rgba(15, 126, 119, 0); }
          100% { transform: scale(0.92); opacity: 0.58; box-shadow: 0 0 0 0 rgba(15, 126, 119, 0); }
        }

        @keyframes nlBeat {
          0%, 100% { transform: translateY(0); opacity: 0.55; }
          50% { transform: translateY(-2px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
